import { useEffect, useState } from 'react';

import { api } from '../api/client';
import { deviceHint, isInAppBrowser, isMobile } from '../lib/browserEnv';
import { isUserCancel, passkeySupported, registerPasskey } from '../lib/passkey';
import { useSessionStore } from '../store/session';
import { addToHomeEligible } from './AddToHomeBanner';

/**
 * 폰에 패스키 하나 심으라는 넛지 — 하단 고정 배너.
 *
 * **노리는 건 데스크톱이다.** 회사 PC는 지문·Windows Hello가 없는 경우가 많아 패스키를
 * 그 자리에서 만들지 못하고, 브라우저가 종료 시 쿠키를 지우도록 설정돼 있으면 세션도 남지
 * 않아 매번 인증번호를 다시 받게 된다(실제 컴플레인). 폰에 패스키가 하나 있으면 데스크톱
 * 크롬이 QR 교차기기로 그걸 쓰므로, 메일 왕복 없이 스캔 한 번으로 로그인된다.
 * 그래서 등록 유도는 **모바일에서** 한다. 공용 PC에 자격증명을 남기지 않는 장점도 있다.
 *
 * 뜨는 조건: 모바일 · 패스키 지원 · 인앱 브라우저 아님(웹뷰는 WebAuthn이 막힌다) ·
 * 등록된 패스키 0개 · 최근에 닫지 않음. 홈 화면 추가 배너가 뜰 자리면 양보한다.
 */

const DISMISS_KEY = 'tomato_passkey_nudge_dismissed_at';
/** 닫아도 이 기간이 지나면 한 번 더 권한다. 재로그인의 불편은 잊을 만하면 다시 오니까. */
const RESHOW_AFTER_DAYS = 14;

function dismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number.parseInt(raw, 10);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < RESHOW_AFTER_DAYS * 86_400_000;
  } catch {
    return true; // storage가 막힌 환경(프라이빗 모드 등)에선 조용히 안 띄운다.
  }
}

/** 기기·설정만으로 판정되는 조건. 여기서 걸리면 서버에 물어보지도 않는다. */
function envEligible(): boolean {
  return (
    isMobile() &&
    passkeySupported() &&
    !isInAppBrowser() &&
    !dismissedRecently() &&
    !addToHomeEligible()
  );
}

export function PasskeyNudgeBanner() {
  const me = useSessionStore((s) => s.me);
  const status = useSessionStore((s) => s.status);
  const onboarded = useSessionStore((s) => s.onboarded);
  const ready = status === 'authenticated' && onboarded && me !== null;

  /** null = 아직 모름(배너 숨김). 서버가 "0개"라고 답해야 비로소 띄운다. */
  const [needed, setNeeded] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ready || !envEligible()) return;
    let cancelled = false;
    void api
      .get<{ passkeys: unknown[] }>('/api/passkeys')
      .then((res) => {
        if (!cancelled) setNeeded(res.passkeys.length === 0);
      })
      .catch(() => {
        /* 조회 실패면 그냥 안 띄운다 — 넛지 하나 못 띄우는 건 조용히 넘어갈 일이다. */
      });
    return () => {
      cancelled = true;
    };
  }, [ready]);

  if (!needed) return null;

  async function add() {
    setBusy(true);
    setError('');
    try {
      await registerPasskey(deviceHint());
      setDone(true);
      // 성공 문구를 잠깐 보여주고 접는다.
      setTimeout(() => setNeeded(false), 2600);
    } catch (err) {
      if (isUserCancel(err)) return; // 사용자가 취소 — 조용히
      setError('등록하지 못했어요. 설정에서 다시 시도할 수 있어요.');
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    setNeeded(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* 저장 실패면 다음 방문에 또 뜰 뿐 — 치명적이지 않다. */
    }
  }

  return (
    // AppShell의 하단 고정 스택 안에서 렌더된다 — 컨테이너가 클릭을 막으므로 스스로 되살린다.
    <div className="pointer-events-auto px-4 pb-3 sm:pb-[max(env(safe-area-inset-bottom),12px)]">
      <div className="mx-auto flex max-w-2xl items-center gap-3 rounded-2xl bg-ink-900/95 px-4 py-3 text-ink-50 shadow-xl backdrop-blur">
        <span className="text-[22px]">🔑</span>
        {done ? (
          <p className="min-w-0 flex-1 text-[13px] leading-snug">
            등록됐어요 ✓ 이제 PC에서 로그인할 때 <b>QR을 이 폰으로 스캔</b>하면 끝이에요.
          </p>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] leading-snug">
                <b>회사 PC에서도 인증번호 없이</b>
                <br />
                <span className="text-ink-50/70">
                  {error || '이 폰에 패스키를 등록해두면 PC 로그인은 QR 스캔 한 번이면 돼요.'}
                </span>
              </p>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => void add()}
              className="shrink-0 rounded-full bg-tomato-600 px-3.5 py-1.5 text-[13px] font-medium text-white transition disabled:opacity-60"
            >
              {busy ? '등록 중…' : '등록'}
            </button>
          </>
        )}
        <button
          type="button"
          aria-label="닫기"
          onClick={dismiss}
          className="shrink-0 rounded-full px-2 py-1 text-[13px] text-ink-50/60 transition hover:text-ink-50"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
